// src/components/ProductDetails.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProduct } from '../hooks/useProduct';
import './ProductDetails.css';
import { useCart } from '../context/CartContext';
import { toggleWishlist, isWishlisted } from '../utils/localWishlist';
import Productlist from './Productlist';
// import ProductReviews from './ProductReviews'; 

/* ═══ SVG Icons ═══ */
const base = (s, w) => ({ width: s, height: s, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: w, strokeLinecap: 'round', strokeLinejoin: 'round' });
const IcChevronLeft = ({ size = 20, sw = 2 }) => (<svg {...base(size, sw)}><path d="m15 18-6-6 6-6" /></svg>);
const IcChevronRight = ({ size = 20, sw = 2 }) => (<svg {...base(size, sw)}><path d="m9 18 6-6-6-6" /></svg>);
const IcClose = ({ size = 22, sw = 1.9 }) => (<svg {...base(size, sw)}><path d="M18 6 6 18M6 6l12 12" /></svg>);
const IcHeart = ({ size = 16, sw = 1.8, filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 5.6a5.2 5.2 0 0 0-7.4 0L12 7l-1.4-1.4a5.2 5.2 0 1 0-7.4 7.4L12 21l8.8-8a5.2 5.2 0 0 0 0-7.4Z" />
  </svg>
);
const IcCart = ({ size = 17, sw = 1.7 }) => (<svg {...base(size, sw)}><path d="M3 4h2.2l2.2 11.2a1.8 1.8 0 0 0 1.77 1.42h8.66a1.8 1.8 0 0 0 1.76-1.4L21 8H6" /><circle cx="9.5" cy="20" r="1.3" /><circle cx="17.5" cy="20" r="1.3" /></svg>);
const IcBolt = ({ size = 16, sw = 1.7 }) => (<svg {...base(size, sw)}><path d="M13 3 4.5 13H11l-1 8 8.5-10H12l1-8Z" /></svg>);
const IcLock = ({ size = 14, sw = 1.8 }) => (<svg {...base(size, sw)}><rect x="4.6" y="10.4" width="14.8" height="9.8" rx="2.6" /><path d="M8.2 10.4V7.9a3.8 3.8 0 0 1 7.6 0v2.5" /></svg>);
const IcTruck = ({ size = 15, sw = 1.7 }) => (<svg {...base(size, sw)}><path d="M2.5 7.2A1.7 1.7 0 0 1 4.2 5.5H14v10.2H2.5Z" /><path d="M14 9h3.3a2 2 0 0 1 1.68.92l2.02 3.08v2.7H14" /><circle cx="7" cy="18" r="1.8" /><circle cx="17.4" cy="18" r="1.8" /></svg>);
const IcCheck = ({ size = 14, sw = 2 }) => (<svg {...base(size, sw)}><path d="m5 12.6 4.5 4.4L19 7" /></svg>);
const IcAlert = ({ size = 20, sw = 1.8 }) => (<svg {...base(size, sw)}><path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>);
const IcZoom = ({ size = 16, sw = 1.7 }) => (<svg {...base(size, sw)}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5M11 8.5v5M8.5 11h5" /></svg>);
const StarFilled = ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="m12 3.5 2.55 5.17 5.7.83-4.13 4.02.98 5.68L12 16.53 6.9 19.2l.98-5.68L3.75 9.5l5.7-.83Z" /></svg>);
const StarEmpty = ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3.5 2.55 5.17 5.7.83-4.13 4.02.98 5.68L12 16.53 6.9 19.2l.98-5.68L3.75 9.5l5.7-.83Z" /></svg>);

/* ═══ Inline SVG placeholder ═══ */
const PLACEHOLDER = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">' +
  '<rect width="400" height="400" fill="#f5f2ec"/>' +
  '<g fill="none" stroke="#c8a96e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M80 140 L200 90 L320 140 L320 280 L200 330 L80 280 Z" opacity="0.5"/>' +
  '<path d="M80 140 L200 190 L320 140 M200 190 L200 330" opacity="0.5"/>' +
  '</g>' +
  '<text x="200" y="370" text-anchor="middle" font-family="Georgia" font-size="14" fill="#8a8270">No Image</text>' +
  '</svg>'
);

/* ═══ Helper: tag label ═══ */
const getTagLabel = (tag) => {
  if (!tag) return '';
  if (typeof tag === 'string') return tag;
  return tag.name || tag.slug || '';
};

/* ═══ Helper: resolve image URL (relative → absolute) ═══ */
const API_ORIGIN = 'http://127.0.0.1:8000';
const resolveImg = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

/* ═══ Helper: build gallery from product ═══ */
const buildGallery = (product) => {
  if (!product) return [{ src: PLACEHOLDER, alt: 'Product' }];
  const seen = new Set();
  const imgs = [];

  // 1) ProductImage array
  if (Array.isArray(product.images)) {
    product.images.forEach((img, i) => {
      const raw = typeof img === 'string' ? img : (img?.image || img?.url);
      const src = resolveImg(raw);
      if (src && !seen.has(src)) {
        seen.add(src);
        imgs.push({
          src,
          alt: (typeof img === 'object' && img?.alt_text) || product.name || `Image ${i + 1}`,
        });
      }
    });
  }

  // 2) primary_image
  const primary = resolveImg(product.primary_image);
  if (primary && !seen.has(primary)) {
    seen.add(primary);
    imgs.unshift({ src: primary, alt: product.name || 'Product' });
  }

  // 3) image (cover)
  const cover = resolveImg(product.image);
  if (cover && !seen.has(cover)) {
    seen.add(cover);
    imgs.unshift({ src: cover, alt: product.name || 'Product' });
  }

  // 4) Fallback
  if (imgs.length === 0) {
    imgs.push({ src: PLACEHOLDER, alt: product.name || 'Product' });
  }
  return imgs;
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
export default function ProductDetails() {
  const { slug } = useParams();
  const { product, loading, error } = useProduct(slug);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [wished, setWished] = useState(false);
  const [activeSize, setActiveSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [imgError, setImgError] = useState({});
  const [qty, setQty] = useState(1);
  

  useEffect(() => {
    if (product?.id) setWished(isWishlisted(product.id));
  }, [product?.id]);

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    setActiveSize(null);
    setImgError({});
  }, [product?.id]);

  // ✅ Debug log
  useEffect(() => {
    if (product) {
      console.log('🔍 Product Image Debug:', {
        image: product.image,
        primary_image: product.primary_image,
        images: product.images,
        images_length: product.images?.length,
      });
    }
  }, [product]);

  const galleryImages = useMemo(() => buildGallery(product), [product]);

  const goPrev = useCallback(() => {
    setActiveImg((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  const goNext = useCallback(() => {
    setActiveImg((i) => (i + 1) % galleryImages.length);
  }, [galleryImages.length]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, goPrev, goNext]);

  if (loading) return (<div className="pd-loading"><div className="pd-spinner" />Loading product…</div>);
  if (error) return (<div className="pd-error"><IcAlert />{error}</div>);
  if (!product) return <div className="pd-empty">Product not found</div>;

  const mainImage = galleryImages[activeImg] || galleryImages[0];
  const handleImgError = (idx) => setImgError((p) => ({ ...p, [idx]: true }));
  const getSrc = (img, idx) => (imgError[idx] ? PLACEHOLDER : (img?.src || PLACEHOLDER));

  const handleAddToCart = () => addToCart(product, qty);
  
  const handleWishlist = () => { const res = toggleWishlist(product); setWished(res.wished); };

  const rating = Number(product.rating || 0);
  // const reviewCount = product.review_count ?? 0;
  const inStock = !!product.is_available;
  const formatPrice = (v) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v ?? 0);


  const handleBuyNow = () => {
  // Buy Now → straight to checkout with only this item
  navigate('/checkout', {
    state: {
      buyNow: true,
      item: {
        product_id: product.id,
        id: product.id,
        name: product.name,
        price: Number(product.price),
        old_price: product.old_price ? Number(product.old_price) : null,
        image: product.primary_image || product.image,
        slug: product.slug,
        tax_percentage: Number(product.tax_percentage || 0),
        quantity: qty,
        size: activeSize,
      },
    },
  });
};
  return (
    <div className="pd-page">
      <div className="pd-shell">
        <nav className="pd-crumb">
          <span className="pd-crumb-link" onClick={() => navigate('/')}>Home</span>
          {product.category_name && (<><span className="pd-crumb-sep">/</span><span className="pd-crumb-link">{product.category_name}</span></>)}
          <span className="pd-crumb-sep">/</span>
          <span className="pd-crumb-current">{product.name}</span>
        </nav>

        <div className="pd-wrap">
          {/* Gallery */}
          <div className="pd-gallery">
            {product.badge && <span className="pd-badge">{product.badge}</span>}

            <div className="pd-thumbs">
              {galleryImages.map((img, i) => (
                <button key={i} type="button"
                  className={`pd-thumb ${activeImg === i ? 'active' : ''}`}
                  onClick={() => setActiveImg(i)}
                  onMouseEnter={() => setActiveImg(i)}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={getSrc(img, i)} alt={img.alt} loading="lazy" onError={() => handleImgError(i)} />
                </button>
              ))}
            </div>

            <div className="pd-main" onClick={() => setLightbox(true)}>
              <img src={getSrc(mainImage, activeImg)} alt={mainImage?.alt || product.name} onError={() => handleImgError(activeImg)} />
              <span className="pd-zoom"><IcZoom size={13} /> Zoom</span>

              {galleryImages.length > 1 && (
                <>
                  <button className="pd-nav pd-nav-left" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Previous"><IcChevronLeft size={18} /></button>
                  <button className="pd-nav pd-nav-right" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Next"><IcChevronRight size={18} /></button>
                  <span className="pd-img-count">{activeImg + 1} / {galleryImages.length}</span>
                </>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="pd-info">
            {product.brand && (<p className="pd-brand"><span>Brand</span><b>{product.brand}</b></p>)}
            <h1 className="pd-name">{product.name}</h1>

            <div className="pd-rating">
              <div className="pd-stars">
                {[1, 2, 3, 4, 5].map((s) =>
                  s <= Math.round(rating) ? (<span key={s} className="pd-star filled"><StarFilled size={14} /></span>) : (<span key={s} className="pd-star"><StarEmpty size={14} /></span>)
                )}
              </div>
              <span className="pd-rating-num">{rating.toFixed(1)}</span>
              {/* <span className="pd-dot-sep" /> */}
              {/* <span className="pd-review-count">{reviewCount} ratings</span> */}
            </div>

            <div className="pd-hr" />

            <div className="pd-price-row">
              <span className="pd-cur"><sup>₹</sup>{formatPrice(product.price)}</span>
              {product.old_price && (<span className="pd-old">₹{formatPrice(product.old_price)}</span>)}
              {product.discount_percentage && (<span className="pd-off">{product.discount_percentage}% off</span>)}
            </div>
            <p className="pd-tax">Inclusive of all taxes</p>

            <div className={`pd-stock-chip ${inStock ? 'ok' : 'no'}`}>
              {inStock ? <IcCheck size={13} /> : <IcAlert size={13} />}
              {inStock ? 'In stock' : 'Out of stock'}
            </div>

            <div className="pd-hr" />

            {product.description && (<><p className="pd-label">About this item</p><p className="pd-desc">{product.description}</p><div className="pd-hr" /></>)}

            {product.sizes?.length > 0 && (
              <div className="pd-sizes">
                <p className="pd-label">Select size</p>
                <div className="pd-size-grid">
                  {product.sizes.map((s) => (
                    <button key={s.id} className={`pd-size ${activeSize === s.size ? 'active' : ''} ${s.stock === 0 ? 'out' : ''}`}
                      onClick={() => s.stock > 0 && setActiveSize(s.size)} disabled={s.stock === 0}>{s.size}</button>
                  ))}
                </div>
              </div>
            )}

            {product.tags?.length > 0 && (
              <div className="pd-tags">
                {product.tags.map((tag, i) => {
                  const label = getTagLabel(tag);
                  if (!label) return null;
                  return <span key={tag?.id || i} className="pd-tag">#{label}</span>;
                })}
              </div>
            )}
          </div>

          {/* Buy Box */}
          <aside className="pd-buy">
            <div className="pd-buy-card">
              <div className="pd-buy-price">
                <span className="pd-buy-cur"><sup>₹</sup>{formatPrice(product.price)}</span>
                {product.old_price && (<span className="pd-buy-old">₹{formatPrice(product.old_price)}</span>)}
              </div>
              {product.discount_percentage && (<span className="pd-buy-off">You save {product.discount_percentage}%</span>)}
              <div className="pd-buy-delivery"><IcTruck size={15} /><span><b>Free delivery</b></span></div>

              <div className="pd-buy-qty">
                <label htmlFor="pd-qty">Quantity</label>
                <select id="pd-qty" value={qty} onChange={(e) => setQty(Number(e.target.value))} disabled={!inStock}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </div>

              <button className="pd-btn pd-btn-cart" onClick={handleAddToCart} disabled={!inStock}><IcCart size={16} /> Add to Cart</button>
              <button className="pd-btn pd-btn-buy" onClick={handleBuyNow} disabled={!inStock}><IcBolt size={16} /> Buy Now</button>
              <button className={`pd-btn pd-btn-wish ${wished ? 'wished' : ''}`} onClick={handleWishlist} aria-label="Toggle wishlist">
                <IcHeart size={16} filled={wished} />{wished ? 'Wishlisted' : 'Add to Wishlist'}
              </button>

              {product.category_name && (<p className="pd-buy-cat">Category · <span>{product.category_name}</span></p>)}
              <p className="pd-secure"><IcLock size={12} /> Secure transaction · SSL encrypted</p>
            </div>
          </aside>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="pd-lightbox" onClick={() => setLightbox(false)}>
          <button className="pd-lb-close" onClick={() => setLightbox(false)} aria-label="Close"><IcClose size={22} /></button>
          {galleryImages.length > 1 && (<button className="pd-lb-nav left" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label="Previous"><IcChevronLeft size={22} /></button>)}
          <img src={getSrc(mainImage, activeImg)} alt={mainImage?.alt || product.name} onClick={(e) => e.stopPropagation()} />
          {galleryImages.length > 1 && (<button className="pd-lb-nav right" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label="Next"><IcChevronRight size={22} /></button>)}
          {galleryImages.length > 1 && (
            <div className="pd-lb-dots">
              {galleryImages.map((_, i) => (<span key={i} className={`pd-lb-dot ${activeImg === i ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setActiveImg(i); }} />))}
            </div>
          )}
        </div>
      )}

      {/* <ProductReviews product={product} /> */}
      <Productlist />
    </div>
  );
}